import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { BaseApiClient } from '@app/core/services/base-api-client.service';
import { Environment } from '@env/environment';
import { LogService } from '@app/core/services/log.service';
import { catchError, map, Observable, tap } from 'rxjs';
import { LoadingIndicatorService } from '@app/core/services/loading-indicator.service';
import { Guid } from '@app/shared/models/guid';

@Injectable({
	providedIn: 'root',
})
export class CertificateService extends BaseApiClient {
	/**
	 * The base URL for the API domain.
	 */
	public baseUrl: string;

	/**
	 * The URL to the API endpoint.
	 */
	public url: string;

	constructor(
		private readonly http: HttpClient,
		private readonly log: LogService,
		private readonly loadingIndicatorService: LoadingIndicatorService,
		environment: Environment
	) {
		super();
		this.baseUrl = environment.services['employee'];
		this.url = this.baseUrl + 'Certificate';
	}
	/**
	 * Get Audit Certificate
	 * @param type
	 * @param scheduledAuditId
	 */
	getAuditCertificate(type: string, scheduledAuditId: Guid): Observable<any> {
		const url = `${this.url}/Audit?type=${type}&auditId=${scheduledAuditId}`;
		const errorHandler = this.createErrorResponseHandler(this.log, undefined, (_) => this.loadingIndicatorService.stopLoading());
		this.loadingIndicatorService.startLoading();
		return this.http.get(url, { observe: 'response', responseType: 'blob' }).pipe(
			tap((result) => {
				this.loadingIndicatorService.stopLoading();
			}),
			catchError(errorHandler)
		);
	}
	/**
	 * Get Filename From Content Disposition
	 * @param contentDisposition
	 * @param title
	 */
	private getFilenameFromContentDisposition(contentDisposition: string, title: string): string {
		if (!contentDisposition) {
			return title;
		}

		// Regular expression to match the filename parameter
		const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
		const matches = filenameRegex.exec(contentDisposition);

		if (matches != null && matches[1]) {
			let filename = matches[1];

			// Decode URL encoded characters in filename if present
			filename = decodeURIComponent(filename);

			// Remove any surrounding quotes
			filename = filename.replace(/['"]/g, '');

			// Handle nested quotes if any
			if (filename.includes('filename=')) {
				const nestedFilenameMatch = /filename=\"(.+?)\"/.exec(filename);
				if (nestedFilenameMatch != null && nestedFilenameMatch[1]) {
					filename = nestedFilenameMatch[1];
				}
			}

			return filename;
		}

		return title;
	}
	/**
	 * Get Training Certificate
	 * @param type
	 * @param subtype
	 * @param studentId
	 */
	getTrainingCertificate(studentId: string): Observable<any> {
		const url = `${this.url}/Training?studentId=${studentId}`;
		const errorHandler = this.createErrorResponseHandler(this.log, undefined, (_) => this.loadingIndicatorService.stopLoading());
		this.loadingIndicatorService.startLoading();
		return this.http.get(url, { observe: 'response', responseType: 'blob' }).pipe(
			tap((result) => {
				this.loadingIndicatorService.stopLoading();
			}),
			catchError(errorHandler)
		);
	}
	/**
	 * Download Certificate
	 * @param title
	 * @param response
	 */
	downloadCert(title, response: any): void {
		const contentDisposition = response.headers.get('content-disposition');
		let filename = this.getFilenameFromContentDisposition(contentDisposition, title);

		const blob = response.body;
		const downloadLink = document.createElement('a');
		downloadLink.href = URL.createObjectURL(blob);
		downloadLink.download = filename;
		downloadLink.click();

		// Cleanup
		URL.revokeObjectURL(downloadLink.href);
	}
}
